import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { first, map, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { MonetaryValueResponse } from '../../../api/core/models/api/monetary-value-response';
import { ClientParameterDetails } from '../../../model/client-model/client-parameter';
import { inventoriesEffectsActions, inventoriesFacadeActions } from '../../../store/inventories/inventories.actions';
import {
  selectBaselineInventory,
  selectInventories,
  selectInventoriesLoadingError,
  selectIsInventoriesLoading,
  selectVariableLastSaved,
  selectVariableLogsByValueId,
} from '../../../store/inventories/inventories.selectors';
import { InventoriesState, InventoryWithValues, Variable } from '../../../store/inventories/inventories.types';
import { CommentSaveEnvelope, VariableSaveEnvelope } from './inventories-facade.types';
import { InventoryUpdate } from './inventory-save-manager.service';

@Injectable({ providedIn: 'root' })
export class InventoriesFacadeService {
  private _store = inject(Store);

  get baselineInventory$(): Observable<InventoryWithValues | null> {
    return this._store.select(selectBaselineInventory);
  }

  get isInventoryLoading(): Observable<boolean> {
    return this._store.select(selectIsInventoriesLoading);
  }

  get inventoriesLoadingError(): Observable<InventoriesState['inventoriesLoadingError']> {
    return this._store.select(selectInventoriesLoadingError);
  }

  get inventories$(): Observable<InventoriesState['inventories'] | null> {
    return this._store.select(selectInventories);
  }

  /**
   * @deprecated
   * @param ids
   */
  addMonetaryVariableIds(ids: Map<string, boolean>): void {
    this._store.dispatch(inventoriesFacadeActions.addMonetaryVariableIDs({ ids }));
  }

  /**
   * @deprecated
   * @param commentSaveEnvelope
   */
  createValueComment(commentSaveEnvelope: CommentSaveEnvelope): void {
    this._store.dispatch(inventoriesFacadeActions.createValueComment({ commentSaveEnvelope }));
  }

  /**
   * Observable of the variable logs. Also dispatches an action to load the variable logs.
   * @param variableNameId the name id of the variable to load the logs for
   * @param inventoryId the inventory id of the variable to load the logs for
   * @param id the id of the variable to load the logs for
   * @returns an observable of the variable logs
   */
  getVariableLogs$(variableNameId: string, inventoryId: string, id: string): Observable<ClientParameterDetails> {
    this._store.dispatch(inventoriesFacadeActions.loadVariableLogs({ variableNameId, inventoryId, id }));
    return this._store.select(selectVariableLogsByValueId(id));
  }

  resetInventories(): void {
    return this._store.dispatch(inventoriesEffectsActions.resetInventories());
  }

  saveInventoryValue(update: InventoryUpdate): void {
    return this._store.dispatch(inventoriesFacadeActions.saveInventoryValue(update));
  }

  /**
   * Save a variable with the given updates.
   * @param variableSaveEnvelope contains the request params for the variable to save, and its updated properties
   * @returns an observable of the saved parameter
   * @deprecated Use saveInventoryValue instead
   */
  saveVariable(variableSaveEnvelope: VariableSaveEnvelope): Observable<Variable> {
    const saveId = uuid(); //Since we support queuing of saves, this identifier is used to ensure the correct save is returned from state
    const variableSaveEnvelopeWithSaveId = { ...variableSaveEnvelope, saveId };
    this._store.dispatch(
      inventoriesFacadeActions.saveVariable({ variableSaveEnvelope: variableSaveEnvelopeWithSaveId })
    );

    return this._store.select(selectVariableLastSaved).pipe(
      filter(
        (variableLastSaved): variableLastSaved is Variable & { saveId: string } =>
          !!variableLastSaved && variableLastSaved.saveId === saveId
      ),
      first(),
      map((variableLastSaved) => {
        // Since we've filtered, TypeScript knows variableLastSaved is not null and has a saveId property
        const { saveId, ...clientVariable } = variableLastSaved;
        return clientVariable;
      })
    );
  }

  // getMonetaryValuesByYear(year: number): Observable<MonetaryValueResponse[]> {
  //   return this._store.select(selectMonetaryValuesByYear(year));
  // }

  // getVariableValuesByYear(year: number): Observable<ValueResponse[]> {
  //   return this._store.select(selectVariableValuesByYear(year));
  // }

  // getAllVariablesByYear(year: number): Observable<(ValueResponse | MonetaryValueResponse)[]> {
  //   return this._store.select(selectAllVariablesByYear(year));
  // }

  // This is currently a placeholder for the actual implementation
  // In the future we also might need to do batch updates
  saveInventoryMonetaryValue(inventoryId: string, monetaryValue: MonetaryValueResponse) {
    this._store.dispatch(inventoriesFacadeActions.saveInventoryMonetaryValue({ inventoryId, monetaryValue }));
  }
}
